import { useEffect, useRef, useState } from "react";

export type CalloutAlignment = "left" | "right" | "xs-right";
export type CalloutArrowPosition = "left" | "right" | "center" | "xs-center";

interface DynamicPositionResult {
    align: CalloutAlignment;
    arrowPosition: CalloutArrowPosition;
}

export const useDynamicCalloutPosition = (): [
    React.RefObject<HTMLDivElement>,
    DynamicPositionResult,
] => {
    const elementRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<DynamicPositionResult>({
        align: "left",
        arrowPosition: "left",
    });

    useEffect(() => {
        const updatePosition = () => {
            if (!elementRef.current) return;

            const element = elementRef.current;
            const rect = element.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const elementCenter = rect.left + rect.width / 2;
            const isInLeftHalf = elementCenter < viewportWidth / 2;

            if (viewportWidth < 768) {
                setPosition({
                    align: isInLeftHalf ? "left" : "xs-right",
                    arrowPosition: isInLeftHalf ? "left" : "xs-center",
                });
            } else {
                setPosition({
                    align: "left",
                    arrowPosition: "left",
                });
            }
        };

        updatePosition();

        const resizeObserver = new ResizeObserver(updatePosition);
        const mutationObserver = new MutationObserver(updatePosition);

        if (elementRef.current) {
            resizeObserver.observe(elementRef.current);
            mutationObserver.observe(elementRef.current.parentNode || document.body, {
                childList: true,
                subtree: true,
            });
        }

        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition);

        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition);
        };
    }, []);

    return [elementRef, position];
};
